import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList, ScatterChart, Scatter, ComposedChart, ReferenceLine, Line } from 'recharts';
import { ProductionTBSData } from './types';
import { getSourceColor, dashboardColors } from './colors';
import { formatNumber } from './utils';
import {
  Box,
  Heading,
  Text,
  Flex,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';

interface ProduksiTBSProps {
  data: ProductionTBSData[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  if (active && payload?.length) {
    const chartData = payload[0]?.payload;
    const priceValue = chartData?.price;
    
    return (
      <Box 
        bg={bgColor} 
        p={3} 
        borderWidth={1} 
        borderColor={borderColor} 
        borderRadius="md" 
        shadow="md"
      >
        <Box fontWeight="semibold">{`${label}`}</Box>
        {payload
          .filter((entry: any) => entry.name !== "Harga")
          .map((entry: any) => (
            <Box key={`tooltip-${entry.name}`} fontSize="sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatNumber(entry.value)} kg`}
            </Box>
          ))
        }
        {priceValue && priceValue > 0 && (
          <Box fontSize="sm" color="red.600" fontWeight="semibold">
            Harga: Rp {formatNumber(priceValue)}
          </Box>
        )}
      </Box>
    );
  }
  return null;
};

const ProduksiTBS: React.FC<ProduksiTBSProps> = ({ data }) => {
  // const formatNumber = (num: number) => {
  //   return num.toLocaleString('id-ID', { 
  //     minimumFractionDigits: 0,
  //     maximumFractionDigits: 0 
  //   });
  // };

  // Ukuran lingkaran dinamis berdasarkan breakpoint
  const dotSize = useBreakpointValue({ 
    base: 12,         // mobile - smaller
    md: 12,           // tablet
    lg: 10,          // laptop
    xl: 10,          // desktop
    '2xl': 12        // large desktop
  }) || 8;

  const rowHeight = useBreakpointValue({ 
      base: '100%',     // mobile - smaller
      md: '100%',       // tablet
      lg: '85%',       // laptop
      xl: '100%',      // desktop
      '2xl': '110%'    // large desktop
  });

  // Custom dot renderer for price scatter
  const renderPriceDot = (props: any) => {
    const { cx, cy, payload } = props;
    const radius = dotSize; // Ukuran lingkaran dinamis
    
    // Only show dot if price > 0
    if (!payload.price || payload.price <= 0) {
      return <g></g>; // Return empty group instead of null
    }
    
    return (
      <g>
        <circle 
          cx={cx} 
          cy={cy} 
          r={radius} // Radius lingkaran dinamis
          fill={dashboardColors.error} // Warna lingkaran
          stroke="#fff" // Border lingkaran
          strokeWidth="2" // Ketebalan border
        />
        <text 
          x={cx} 
          y={cy} 
          fill="#fff" 
          textAnchor="middle" 
          dominantBaseline="middle"
          fontSize={Math.max(8, radius - 2)} // Ukuran font teks dinamis
          fontWeight="bold" // Ketebalan font
        >
          Rp
        </text>
      </g>
    );
  };

  // Transform data for chart
  const suppliers = Array.from(new Set(data.map(d => d.supplier)));
  const chartData = suppliers.map(supplier => {
    const supplierData = data.filter(d => d.supplier === supplier);
    const result: any = { supplier };
    
    supplierData.forEach(item => {
      result[item.source] = item.net_weight;
      // Add price data - use the price from any item for this supplier
      if (item.price > 0) {
        result.price = item.price;
      }
    });
    
    return result;
  });

  // Calculate totals by source
  const internalTotal = data
    .filter(d => d.source === 'Internal')
    .reduce((sum, item) => sum + item.net_weight, 0);
  
  const externalTotal = data
    .filter(d => d.source === 'Eksternal')
    .reduce((sum, item) => sum + item.net_weight, 0);

  // Define common colors once at the top level
  const bgCard = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');

  const chartMinHeight = useBreakpointValue({
      base: '380px', // mobile
      md: '380px',   // tablet
      lg: '300px',   // laptop
      xl: '230px',   // desktop
      '2xl': '290px' // large desktop
    }) || '300px';

  return (
    <Box bg={bgCard} borderRadius="lg" p={4} h="100%" display="flex" flexDirection="column">
      <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px">
        PRODUKSI TBS PER LOKASI
      </Heading>
      
      {/* Summary Cards */}
      {/* <SimpleGrid columns={2} spacing={4} mb={4}>
        <Box bg={useColorModeValue('green.50', 'green.900')} borderWidth={1} borderColor={useColorModeValue('green.200', 'green.700')} borderRadius="lg" p={3}>
          <Text color="green.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Internal
          </Text>
          <Text color={useColorModeValue('green.900', 'green.200')} fontSize="xl" fontWeight="bold">
            {formatNumber(internalTotal)}
          </Text>
          <Text color="green.600" fontSize="xs">kg</Text>
        </Box>
        
        <Box bg={useColorModeValue('orange.50', 'orange.900')} borderWidth={1} borderColor={useColorModeValue('orange.200', 'orange.700')} borderRadius="lg" p={3}>
          <Text color="orange.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Eksternal
          </Text>
          <Text color={useColorModeValue('orange.900', 'orange.200')} fontSize="xl" fontWeight="bold">
            {formatNumber(externalTotal)}
          </Text>
          <Text color="orange.600" fontSize="xs">kg</Text>
        </Box>
      </SimpleGrid> */}
      <Flex align="center" gap={6} mb={1} mt={0} justify="center">
        <Text fontSize="sm" color="#15803D" fontWeight="semibold">
          Internal: <Box as="span" fontWeight="bold" color="#15803D" display="inline">{internalTotal ? formatNumber(internalTotal) : '-'}</Box>
        </Text>
        <Text fontSize="sm" color="#D97706" fontWeight="semibold">
          Eksternal: <Box as="span" fontWeight="bold" color="#D97706" display="inline">{externalTotal ? formatNumber(externalTotal) : '-'}</Box>
        </Text>
      </Flex>

      {/* Chart */}
      <Box minH={chartMinHeight} h={chartMinHeight}>
      {/* <Box minH="0" height="full"> */}
        <ResponsiveContainer width="100%" height={rowHeight}>
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: -50, left: 10, bottom: 0 }}
            barGap={0}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="supplier" 
              fontSize={11}
              interval={0}
              // tick={{ textAnchor: 'end', dy: 5 }}
              // angle={-45}
              height={40}
            />
            {/* Y Axis kiri untuk weight */}
            <YAxis 
              yAxisId="weight"
              fontSize={12}
              tickFormatter={formatNumber}
              label={{
                value: 'Berat (kg)',
                angle: -90,
                position: 'insideLeft',
                offset: -5,
                fontSize: 14,
                fill: '#666',
              }}
            />
            {/* Y Axis kanan untuk price */}
            <YAxis 
              yAxisId="price"
              orientation="right"
              fontSize={12}
              tickFormatter={formatNumber}
              label={{
                value: 'Rp',
                angle: 90,
                position: 'insideRight',
                offset: 8,
                fontSize: 14,
                fill: '#666',
              }}
              
            />
            <Tooltip content={<CustomTooltip />} />
            {/* <Legend /> */}
            <Bar 
              yAxisId="weight"
              dataKey="Internal" 
              fill={getSourceColor('Internal')}
              name="Internal"
              barSize={18}
              stackId="weight"
            />
            <Bar 
              yAxisId="weight"
              dataKey="Eksternal" 
              fill={getSourceColor('Eksternal')}
              name="Eksternal"
              barSize={18}
              stackId="weight"
            />
            {/* Scatter untuk price dots */}
            <Scatter
              yAxisId="price"
              dataKey="price"
              fill={dashboardColors.error}
              name="Harga"
              shape={renderPriceDot}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Detailed List */}
      {/* <Box mt={4}>
        <Text fontSize="sm" fontWeight="semibold" color={useColorModeValue('gray.700', 'gray.300')} mb={2}>
          Detail per Supplier
        </Text>
        <SimpleGrid columns={2} spacing={2} fontSize="xs">
          {data.map((item) => (
            <Flex 
              key={`${item.supplier}-${item.source}`}
              justify="space-between" 
              align="center" 
              p={2} 
              bg={listItemBg} 
              borderRadius="md"
            >
              <Text fontWeight="medium">
                {item.supplier} ({item.source})
              </Text>
              <Text fontFamily="mono">
                {formatNumber(item.net_weight)} kg
              </Text>
            </Flex>
          ))}
        </SimpleGrid>
      </Box> */}
    </Box>
  );
};

export default ProduksiTBS;
